import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Bed, Bath, Square, MapPin, ShieldCheck, ArrowUpRight, Heart, Share2,
  ScanSearch, Flame, Eye, Check, FileCheck2, BadgeCheck, Ruler, CornerUpRight,
  Video, Users, Trees, Dumbbell, Shield, Car, Zap, Calendar, Compass, Home, MoreVertical, X, Trash2
} from "lucide-react";
import { formatPrice, formatArea, api, formatApiError } from "@/lib/api";
import { useCompare } from "@/contexts/CompareContext";
import { useAuth } from "@/contexts/AuthContext";
import { useShortlist } from "@/contexts/ShortlistContext";
import { toast } from "sonner";
import { isPendingSoldStatus, isSoldStatus, prettyLabel, propertyStatus } from "@/lib/propertyUi";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";

const getAmenityIcon = (name) => {
  const n = name.toLowerCase().replace(/_/g, " ");
  if (n.includes("cctv") || n.includes("camera") || n.includes("surveillance")) {
    return <Video className="w-3 h-3 text-blue-500" />;
  }
  if (n.includes("club") || n.includes("community")) {
    return <Users className="w-3 h-3 text-amber-500" />;
  }
  if (n.includes("garden") || n.includes("park") || n.includes("lawn") || n.includes("trees")) {
    return <Trees className="w-3 h-3 text-emerald-500" />;
  }
  if (n.includes("gym") || n.includes("fitness") || n.includes("workout")) {
    return <Dumbbell className="w-3 h-3 text-indigo-500" />;
  }
  if (n.includes("security") || n.includes("guard") || n.includes("gate")) {
    return <Shield className="w-3 h-3 text-rose-500" />;
  }
  if (n.includes("parking") || n.includes("garage") || n.includes("car")) {
    return <Car className="w-3 h-3 text-slate-500" />;
  }
  if (n.includes("power") || n.includes("backup") || n.includes("electricity") || n.includes("generator")) {
    return <Zap className="w-3 h-3 text-yellow-500" fill="currentColor" />;
  }
  return <ShieldCheck className="w-3 h-3 text-[#0078DB]" />;
};

const TRENDING_VIEW_THRESHOLD = 15;
const FALLBACK_IMAGE = "/brand/real-place-mark.svg";
const TIME_SLOTS = ["10:00 AM", "11:30 AM", "1:00 PM", "3:00 PM", "5:00 PM"];

function slotTo24h(slot) {
  if (!slot) return "10:00";
  const [time, mod] = slot.split(" ");
  let [h, m] = time.split(":").map(Number);
  if (mod === "PM" && h !== 12) h += 12;
  if (mod === "AM" && h === 12) h = 0;
  return `${String(h).padStart(2, "0")}:${String(m || 0).padStart(2, "0")}`;
}

export default function PropertyCard({ property: p = {}, onSave, saved, isRecentMobile = false, onRemoveRecent }) {
  const { ids, toggle } = useCompare();
  const { user } = useAuth();
  const { isSaved, toggle: toggleSave } = useShortlist();
  const location = useLocation();
  // V3 §27: detail pages return to the exact search URL (filters, view, page).
  const fromUrl = `${location.pathname}${location.search}`;
  const id = p.property_id || p.id;
  const inCompare = ids.includes(id);
  const isLand = ["LAND", "PLOT", "Residential Land", "Commercial Land"].includes(p.category) || /land|plot/i.test(`${p.category || ""} ${p.subcategory || ""}`);
  const [justShared, setJustShared] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuButtonRef = useRef(null);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!menuOpen) return;
    menuRef.current?.querySelector("button")?.focus();
    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        setMenuOpen(false);
        menuButtonRef.current?.focus();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [menuOpen]);

  const bldg = p.building_details || p.building || {};
  const land = p.land_details || p.land || {};
  const bedrooms = p.bedrooms || bldg.bedrooms || 0;
  const bathrooms = p.bathrooms || bldg.bathrooms || 0;
  const areaVal = p.area || p.area_sqft || land.area || bldg.area || 0;
  const areaUnit = p.area_unit || land.area_unit || bldg.area_unit || "sq_ft";
  const roadWidth = p.road_width_ft || land.road_width_ft || 0;
  const cornerPlot = p.corner_plot === "true" || p.corner_plot === true || land.corner_plot === true || land.corner_plot === "true";
  const facing = p.facing || bldg.facing || land.facing || "";
  const furnishing = p.furnishing || bldg.furnishing_status || bldg.furnishing || "";

  const [bookOpen, setBookOpen] = useState(false);
  const [buyerName, setBuyerName] = useState(user?.name || "");
  const [buyerEmail, setBuyerEmail] = useState(user?.email || "");
  const [buyerPhone, setBuyerPhone] = useState(user?.phone || "");
  const [visitDate, setVisitDate] = useState(new Date(Date.now() + 86400000).toISOString().slice(0, 10));
  const [visitSlot, setVisitSlot] = useState(TIME_SLOTS[0]);
  const [visitNote, setVisitNote] = useState("");
  const [booking, setBooking] = useState(false);

  const handleBookVisit = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) {
      toast.error("Please log in to book a site visit");
      return;
    }
    const role = String(user.role || "").toLowerCase().replace("office_user", "office");
    if (!["buyer", "owner"].includes(role)) {
      toast.error("Site visits can be booked from a buyer or owner account.");
      return;
    }
    setBuyerName(user?.name || "");
    setBuyerEmail(user?.email || "");
    setBuyerPhone(user?.phone || "");
    setBookOpen(true);
  };

  const submitBooking = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setBooking(true);
    try {
      await api.post("/appointments", {
        buyer_name: buyerName,
        buyer_email: buyerEmail,
        buyer_phone: buyerPhone,
        date: visitDate,
        time_slot: visitSlot,
        note: visitNote,
        buyer_user_id: user?.user_id || user?.id,
        property_id: id,
        visit_at: `${visitDate}T${slotTo24h(visitSlot)}:00`
      });
      toast.success("Site visit requested! Agent/office will confirm shortly.");
      setBookOpen(false);
      setVisitNote("");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBooking(false);
    }
  };

  const img = (Array.isArray(p.images) && p.images[0]) || p.thumbnail_url || p.public_url || FALLBACK_IMAGE;
  const imageUnavailable = img === FALLBACK_IMAGE;
  const isTrending = p.trending || (p.views || p.view_count || 0) >= TRENDING_VIEW_THRESHOLD;
  const viewCount = p.views || p.view_count || 0;
  const status = propertyStatus(p.status);
  const isSold = isSoldStatus(status);
  const isPendingSold = isPendingSoldStatus(status);

  // Issue #17: derive promotional tags shown in the card's metadata section.
  const isNew = (() => {
    const listed = p.published_at || p.listed_at || p.created_at || p.createdAt;
    if (!listed) return false;
    const t = new Date(listed).getTime();
    if (Number.isNaN(t)) return false;
    return (Date.now() - t) < 14 * 24 * 60 * 60 * 1000;
  })();
  const isPremium = !!(p.boosted || p.premium || p.is_premium);

  const isRecentlyViewed = (() => {
    try {
      const list = JSON.parse(localStorage.getItem("recently_viewed_properties") || "[]");
      return list.includes(id);
    } catch {
      return false;
    }
  })();

  const localSaved = isSaved(id);

  const handleSave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    toggleSave(id);
    if (onSave) {
      onSave(id);
    }
  };

  const handleShare = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    const url = `${window.location.origin}/properties/${id}`;
    const text = `${p.title || "Property"} · ${formatPrice(p.price)} · ${p.locality || ""}, ${p.city || ""}`;
    try {
      if (navigator.share) await navigator.share({ title: p.title || "Property", text, url });
      else {
        await navigator.clipboard.writeText(url);
        toast.success("Link copied to clipboard");
        setJustShared(true);
        setTimeout(() => setJustShared(false), 1400);
      }
    } catch {
      // ignore cancelled share
    }
  };

  const handleCompare = (e) => {
    e.preventDefault();
    e.stopPropagation();
    toggle(id);
  };

  return (
    <article 
      data-testid={`property-card-${id}`} 
      className={`premium-card premium-card-interactive bg-background border border-border overflow-hidden group hover:-translate-y-1 transition-all duration-300 flex flex-col justify-between h-full ${
        isRecentMobile 
          ? "w-[85vw] min-w-[85vw] max-w-[340px] rounded-[18px] md:w-auto md:min-w-0 md:max-w-none md:rounded-2xl" 
          : "rounded-2xl"
      }`}
    >
      <Link to={`/properties/${id}`} state={{ from: fromUrl }} className={`block relative w-full overflow-hidden ${isRecentMobile ? "h-[200px] md:h-64" : "h-64"}`}>
        <img src={img} alt={imageUnavailable ? "Property image unavailable" : (p.title || "Property image")} className={`w-full h-full transition-transform duration-700 group-hover:scale-105 ${imageUnavailable ? "object-contain bg-slate-100 p-12" : "object-cover"}`} loading="lazy" decoding="async" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/35 via-transparent to-black/10" />

        <div className="absolute top-3 right-3 flex flex-col gap-1.5 z-30">
          {/* Top-right Remove from History X Button (Issue 25) */}
          {onRemoveRecent && (
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onRemoveRecent(id, e);
              }}
              className="w-7 h-7 rounded-full bg-slate-900 hover:bg-red-600 text-white flex items-center justify-center transition shadow-md self-end mb-1"
              title="Remove from history"
              aria-label="Remove from history"
              data-testid={`remove-history-btn-${id}`}
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}

          {/* Desktop view: standardized quick action sequence: Favourite -> Schedule Visit -> Compare -> Share */}
          <div className="hidden md:flex flex-col gap-1.5">
            <IconBtn onClick={handleBookVisit} ariaLabel="Book site visit" testid={`book-visit-btn-${id}`}><Calendar className="w-4 h-4" /></IconBtn>
            <IconBtn onClick={handleSave} ariaLabel={localSaved ? "Remove from shortlist" : "Save to shortlist"} testid={`save-btn-${id}`} active={localSaved}><Heart className="w-4 h-4" fill={localSaved ? "currentColor" : "none"} /></IconBtn>
            <IconBtn onClick={handleShare} ariaLabel="Share property" testid={`share-btn-${id}`} active={justShared}>{justShared ? <Check className="w-4 h-4" /> : <Share2 className="w-4 h-4" />}</IconBtn>
            <IconBtn onClick={handleCompare} ariaLabel="Compare property" testid={`compare-toggle-${id}`} active={inCompare}><ScanSearch className="w-4 h-4" /></IconBtn>
          </div>

          {/* Mobile view: favourite + 3-dot menu */}
          <div className="flex md:hidden gap-1.5 items-center relative z-30">
            <IconBtn onClick={handleSave} ariaLabel={localSaved ? "Remove from shortlist" : "Save to shortlist"} testid={`save-btn-${id}`} active={localSaved}><Heart className="w-4 h-4" fill={localSaved ? "currentColor" : "none"} /></IconBtn>
            
            <div className="relative">
              <IconBtn 
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); setMenuOpen(!menuOpen); }} 
                ariaLabel="More actions" 
                testid={`more-actions-btn-${id}`}
                buttonRef={menuButtonRef}
                ariaExpanded={menuOpen}
                ariaHaspopup="menu"
              >
                <MoreVertical className="w-4 h-4" />
              </IconBtn>
              
              {menuOpen && (
                <>
                  <div className="fixed inset-0 z-40 bg-transparent" aria-hidden="true" onClick={(e) => { e.preventDefault(); e.stopPropagation(); setMenuOpen(false); menuButtonRef.current?.focus(); }} />
                  <div ref={menuRef} role="menu" aria-label="Property actions" className="absolute right-0 mt-2 w-48 bg-white border border-slate-200/85 rounded-xl shadow-xl py-1.5 z-50 animate-in fade-in slide-in-from-top-1 duration-100">
                    <button
                      type="button"
                      role="menuitem"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setMenuOpen(false);
                        handleBookVisit(e);
                      }}
                      className="w-full text-left px-4 py-2 text-xs font-bold text-slate-700 hover:bg-slate-50 flex items-center gap-2"
                    >
                      <Calendar className="w-3.5 h-3.5" /> Book Site Visit
                    </button>
                    <button
                      type="button"
                      role="menuitem"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setMenuOpen(false);
                        handleShare(e);
                      }}
                      className="w-full text-left px-4 py-2 text-xs font-bold text-slate-700 hover:bg-slate-50 flex items-center gap-2"
                    >
                      <Share2 className="w-3.5 h-3.5" /> Share
                    </button>
                    <button
                      type="button"
                      role="menuitem"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setMenuOpen(false);
                        handleCompare(e);
                      }}
                      className={`w-full text-left px-4 py-2 text-xs font-bold flex items-center gap-2 ${inCompare ? "text-[#0078DB]" : "text-slate-700 hover:bg-slate-50"}`}
                    >
                      <ScanSearch className="w-3.5 h-3.5" /> {inCompare ? "In Compare" : "Compare"}
                    </button>
                    {onRemoveRecent && (
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setMenuOpen(false);
                          onRemoveRecent(id, e);
                        }}
                        className="w-full text-left px-4 py-2 text-xs font-bold text-red-600 hover:bg-red-50 flex items-center gap-2 border-t border-slate-100 mt-1"
                      >
                        <Trash2 className="w-3.5 h-3.5" /> Remove from History
                      </button>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </Link>

      <Link to={`/properties/${id}`} state={{ from: fromUrl }} className="block p-5 flex-grow flex flex-col justify-between">
        <div>
          <div className="flex items-start justify-between gap-3 mb-2">
            <div className="flex-grow">
              <div className="min-h-[2.5rem] flex flex-col justify-center">
                <p className="text-xl font-black text-slate-900 font-display tabular-nums leading-none">
                  {formatPrice(p.price)}
                </p>
                <div className="h-4 mt-0.5 select-none flex items-center">
                  {(() => {
                    const ppu = (areaVal && areaVal > 0) ? p.price / areaVal : null;
                    const formattedUnit = areaUnit === "sq_ft" ? "sq ft" : "sq yard";
                    return isLand && ppu ? (
                      <span className="text-[10px] font-semibold text-slate-500">
                        @ {formatPrice(ppu)} / {formattedUnit}
                      </span>
                    ) : (
                      <span className="opacity-0 text-[10px]">&nbsp;</span>
                    );
                  })()}
                </div>
              </div>
              <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mt-0.5">{p.category || "Property"}{p.listing_type ? ` · ${p.listing_type}` : ""}</p>
            </div>
            <ArrowUpRight className="w-4 h-4 text-muted-foreground group-hover:text-primary transition shrink-0" />
          </div>
          <h3 className="font-display font-bold text-base text-primary leading-tight line-clamp-2 mb-2 min-h-[2.4rem]">{p.title || "Property title unavailable"}</h3>
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3 flex items-center gap-1 line-clamp-1"><MapPin className="w-3 h-3" /> {[p.locality, p.city].filter(Boolean).join(", ") || "Location unavailable"}</p>
          {/* Issue #17: all status, activity, promotional, and trust tags live in
              one metadata row so listing photography remains unobstructed. */}
          {(() => {
            const tags = [];
            if (!isSold && !isPendingSold && p.verified_listing) tags.push({ label: "Verified listing", Icon: ShieldCheck, cls: "bg-teal-50 text-teal-700 border-teal-100" });
            else if (!isSold && !isPendingSold && status === "PUBLISHED") tags.push({ label: "Published", Icon: ShieldCheck, cls: "bg-teal-50 text-teal-700 border-teal-100" });
            if (p.verified_owner) tags.push({ label: "Owner", Icon: BadgeCheck, cls: "bg-emerald-50 text-emerald-700 border-emerald-100" });
            if (p.verified_documents) tags.push({ label: "Docs Verified", Icon: FileCheck2, cls: "bg-blue-50 text-blue-700 border-blue-100" });
            if (p.featured && !isSold) tags.push({ label: "Featured", Icon: BadgeCheck, cls: "bg-primary/10 text-primary border-primary/20" });
            if (isPremium && !isSold) tags.push({ label: "Premium", Icon: Flame, cls: "bg-amber-50 text-amber-700 border-amber-100" });
            if (isNew && !isSold && !isPendingSold) tags.push({ label: "New", Icon: Zap, cls: "bg-fuchsia-50 text-fuchsia-700 border-fuchsia-100" });
            if (isTrending && !isSold) tags.push({ label: "Trending", Icon: Flame, cls: "bg-accent text-accent-foreground border-border", testid: `trending-badge-${id}` });
            if (isRecentlyViewed) tags.push({ label: "Recently Seen", Icon: Eye, cls: "bg-secondary text-secondary-foreground border-border", testid: `recently-viewed-badge-${id}` });
            if (viewCount > 0) tags.push({ label: `${Number(viewCount).toLocaleString("en-IN")} views`, Icon: Eye, cls: "bg-muted text-muted-foreground border-border" });
            if (isSold || isPendingSold) tags.push({
              label: prettyLabel(status),
              Icon: ShieldCheck,
              cls: isSold ? "bg-secondary text-secondary-foreground border-border" : "bg-accent text-accent-foreground border-border",
              testid: isSold ? `sold-badge-${id}` : `pending-sold-badge-${id}`,
            });
            return tags.length ? (
              <div className="flex flex-wrap gap-1.5 mb-3" data-testid={`property-tags-${id}`}>
                {tags.map(({ label, Icon, cls, testid }, i) => (
                  <span key={`${label}-${i}`} data-testid={testid} className={`text-[9.5px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full inline-flex items-center gap-1 border ${cls}`}>
                    <Icon className="w-3 h-3" /> {label}
                  </span>
                ))}
              </div>
            ) : null;
          })()}
          {(() => {
            const ld = p.land_details || p.land || {};
            return isLand && (ld.registry_available || ld.mutation_available || ld.gated_colony || ld.boundary_wall) ? (
              <div className="flex flex-wrap gap-1.5 mb-3.5 select-none">
                {ld.registry_available && <span className="text-[9px] font-bold uppercase tracking-wider bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded border border-emerald-100/50">Registry Ready</span>}
                {ld.mutation_available && <span className="text-[9px] font-bold uppercase tracking-wider bg-blue-50 text-blue-700 px-2 py-0.5 rounded border border-blue-100/50">Mutation Done</span>}
                {ld.gated_colony && <span className="text-[9px] font-bold uppercase tracking-wider bg-purple-50 text-purple-700 px-2.5 py-0.5 rounded border border-purple-100/50">Gated Colony</span>}
                {ld.boundary_wall && <span className="text-[9px] font-bold uppercase tracking-wider bg-slate-50 text-slate-600 px-2.5 py-0.5 rounded border border-slate-100/50">Boundary Wall</span>}
              </div>
            ) : null;
          })()}
          {Array.isArray(p.amenities) && p.amenities.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-4">
              {p.amenities.slice(0, 3).map((amt, idx) => (
                <span key={idx} className="bg-primary/5 text-primary text-[10px] font-bold px-2.5 py-0.5 rounded-full capitalize flex items-center gap-1">
                  {getAmenityIcon(amt)}
                  <span>{amt.replace(/_/g, " ")}</span>
                </span>
              ))}
              {p.amenities.length > 3 && (
                <span className="text-[10px] text-muted-foreground font-semibold px-1 py-0.5">
                  +{p.amenities.length - 3} more
                </span>
              )}
            </div>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-muted-foreground border-t border-border pt-3 mt-auto">
          {bedrooms > 0 && <span className="flex items-center gap-1 font-semibold" title="Bedrooms"><Bed className="w-3.5 h-3.5 text-slate-400" />{bedrooms} Bed</span>}
          {bathrooms > 0 && <span className="flex items-center gap-1 font-semibold" title="Bathrooms"><Bath className="w-3.5 h-3.5 text-slate-400" />{bathrooms} Bath</span>}
          <span className="flex items-center gap-1 font-semibold" title="Super Area"><Square className="w-3.5 h-3.5 text-slate-400" />{formatArea(areaVal, areaUnit)}</span>
          {isLand && roadWidth > 0 && <span className="flex items-center gap-1 font-semibold" title="Road Width"><Ruler className="w-3.5 h-3.5 text-slate-400" />{roadWidth} ft road</span>}
          {isLand && cornerPlot && <span className="flex items-center gap-1 font-semibold" title="Corner Plot"><CornerUpRight className="w-3.5 h-3.5 text-slate-400" />Corner</span>}
          {!isLand && furnishing && furnishing !== "—" && <span className="flex items-center gap-1 font-semibold" title="Furnishing Status"><Home className="w-3.5 h-3.5 text-slate-400" />{furnishing}</span>}
          {facing && facing !== "—" && <span className="flex items-center gap-1 font-semibold" title="Facing Direction"><Compass className="w-3.5 h-3.5 text-slate-400" />Facing {facing}</span>}
        </div>
      </Link>

      <Dialog open={bookOpen} onOpenChange={setBookOpen}>
        <DialogContent data-testid="appointment-dialog" onClick={(e) => e.stopPropagation()}>
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Book a property visit</DialogTitle>
            <DialogDescription>
              Submit a request for a site visit to: <span className="font-semibold text-primary">{p.title}</span>
            </DialogDescription>
          </DialogHeader>
          <form className="space-y-3 pt-2" onSubmit={submitBooking}>
            <div>
              <Label>Your Name</Label>
              <Input value={buyerName} onChange={(e) => setBuyerName(e.target.value)} required data-testid="appt-name" />
            </div>
            <div>
              <Label>Email</Label>
              <Input type="email" value={buyerEmail} onChange={(e) => setBuyerEmail(e.target.value)} data-testid="appt-email" />
            </div>
            <div>
              <Label>Phone</Label>
              <Input value={buyerPhone} onChange={(e) => setBuyerPhone(e.target.value)} required data-testid="appt-phone" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label>Date</Label>
                <Input type="date" min={new Date().toISOString().slice(0, 10)} value={visitDate} onChange={(e) => setVisitDate(e.target.value)} required data-testid="appt-date" />
              </div>
              <div>
                <Label>Time</Label>
                <select className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" value={visitSlot} onChange={(e) => setVisitSlot(e.target.value)} required data-testid="appt-time">
                  {TIME_SLOTS.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea value={visitNote} onChange={(e) => setVisitNote(e.target.value)} rows={2} data-testid="appt-notes" />
            </div>
            <Button type="submit" disabled={booking} className="w-full font-bold" data-testid="appt-submit">
              {booking ? "Booking…" : "Confirm Visit Request"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </article>
  );
}

function IconBtn({ children, onClick, ariaLabel, testid, active, buttonRef, ariaExpanded, ariaHaspopup }) {
  return (
    <button ref={buttonRef} type="button" onClick={onClick} aria-label={ariaLabel} aria-expanded={ariaExpanded} aria-haspopup={ariaHaspopup} data-testid={testid} className={`w-9 h-9 rounded-full grid place-items-center transition transform hover:scale-110 active:scale-95 ${active ? "bg-primary text-primary-foreground shadow" : "bg-white text-primary hover:bg-slate-50"}`}>
      {children}
    </button>
  );
}
